import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { NotificationReadEvent } from 'src/app/models/task.model';
import { TasksWebSocketService } from 'src/app/services/tasks-websocket.service';
import { PushDeviceService } from './push-device.service';
import {
  closePushByTags,
  resolvePushTagsFromReadEvent,
} from '../utils/push-notification.util';

export interface TaskUnreadUpdate {
  task_id: number;
  unread_count: number;
}

/**
 * Cross-device notification + task badge sync via WebSocket `notification_read`.
 */
@Injectable({ providedIn: 'root' })
export class NotificationSyncService implements OnDestroy {
  private readonly taskUnreadUpdates$ = new Subject<TaskUnreadUpdate>();
  private readonly initialized$ = new BehaviorSubject(false);
  private subs: Subscription[] = [];

  constructor(
    private readonly ws: TasksWebSocketService,
    private readonly pushDevice: PushDeviceService,
  ) {}

  /** Call once after login (AppComponent). Idempotent. */
  init(): void {
    if (this.initialized$.value) {
      return;
    }
    if (!this.isSignedIn()) {
      return;
    }

    this.ws.connect();

    this.subs.push(
      this.ws.onNotificationRead().subscribe((payload) => {
        void this.handleNotificationRead(payload);
      }),
    );

    this.subs.push(
      this.ws.onUnreadCountUpdated().subscribe(({ task_id, unread_count }) => {
        this.emitTaskUnread(task_id, unread_count);
      }),
    );

    this.initialized$.next(true);
  }

  onTaskUnreadUpdates() {
    return this.taskUnreadUpdates$.asObservable();
  }

  emitTaskUnread(taskId: number, unreadCount: number): void {
    if (taskId == null) {
      return;
    }
    this.taskUnreadUpdates$.next({ task_id: taskId, unread_count: unreadCount });
  }

  async handleNotificationRead(payload: NotificationReadEvent): Promise<void> {
    const ids = payload.notification_ids || [];
    if (ids.length) {
      this.pushDevice.markNotificationsReadByIds(ids, payload.read_at);
    }

    if (payload.inbox_unread_count != null) {
      this.pushDevice.setInboxUnreadCount(payload.inbox_unread_count);
    }

    if (payload.task_id != null && payload.task_unread_count != null) {
      this.emitTaskUnread(payload.task_id, payload.task_unread_count);
    }

    const tags = resolvePushTagsFromReadEvent(payload, this.pushDevice.getInboxItems());
    await closePushByTags(tags);
  }

  reset(): void {
    this.initialized$.next(false);
    this.subs.forEach((s) => s.unsubscribe());
    this.subs = [];
  }

  ngOnDestroy(): void {
    this.reset();
  }

  private isSignedIn(): boolean {
    try {
      return !!localStorage.getItem('token');
    } catch {
      return false;
    }
  }
}
